/**
 * Checks whether the passed variable is an object (arrays and functions are not considered to be object in this case)
 * @param valueToCheck any variable you would like to determine is or is not an object
 * @returns a boolean that is true if the passed variable is an object and false if it is not an object
 */
export const isNonNullableObject = (
  valueToCheck: unknown
): valueToCheck is Record<string, unknown> => {
  return (
    typeof valueToCheck === 'object' &&
    valueToCheck !== null &&
    !Array.isArray(valueToCheck) &&
    typeof valueToCheck !== 'function'
  )
}

/**
 * Recursively loops through an object recording all keys that return undefined values.
 * @param object object you would like to filter for undefined values
 * @returns an array with the object keys that returned undefined
 */
export const getUndefinedPropertyKeys = (object: Record<string, unknown>): Promise<string[]> => {
  return new Promise((resolve) => {
    const undefinedProperties: string[] = []

    const recordUndefinedValues = (object: Record<string, unknown>) => {
      if (!isNonNullableObject(object)) return []
      const objectKeys = Object.keys(object)
      if (objectKeys.length === 0) return []

      for (const key in object) {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
          const objectProperty = object[key]

          if (objectProperty === undefined) {
            undefinedProperties.push(key)
          } else if (Array.isArray(objectProperty)) {
            const undefinedPropertiesSubArray = objectProperty.reduce((accumulator, item) => {
              return accumulator.concat(recordUndefinedValues(item))
            }, [])
            undefinedProperties.concat(undefinedPropertiesSubArray)
          } else if (isNonNullableObject(objectProperty)) {
            undefinedProperties.concat(recordUndefinedValues(objectProperty))
          }
        }
      }

      return []
    }

    recordUndefinedValues(object)

    resolve(undefinedProperties)
  })
}

export class UndefinedValueError extends Error {
  undefinedProperties: string[]

  constructor(message: string, undefinedPropertiesArray: string[]) {
    super(message)

    this.name = 'UndefinedValueError'

    this.undefinedProperties = undefinedPropertiesArray
  }
}
